import React from "react";
import { Button, Menu, MenuItem, Box } from "@mui/material";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import LibraryBooksOutlinedIcon from "@mui/icons-material/LibraryBooksOutlined";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import SearchIcon from "@mui/icons-material/Search";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import { IconGradient, SmallTextGradient } from "../Gradient";

type CommandPaletteProps = {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  setActive: (key: string) => void;
  userId?: string;
  cmdPaletteAnchorRef: React.RefObject<HTMLButtonElement>;
  navigate: (path: string) => void;
};

const CommandPalette: React.FC<CommandPaletteProps> = ({
  anchorEl,
  onClose,
  setActive,
  userId,
  cmdPaletteAnchorRef,
  navigate,
}) => {
  return (
    <>
      <Button
        ref={cmdPaletteAnchorRef}
        sx={{ position: 'absolute', width: 1, height: 1, p: 0, minWidth: 0, opacity: 0, pointerEvents: 'none' }}
        aria-hidden
        tabIndex={-1}
      />
      <Menu
        id="command-palette"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={onClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{
          elevation: 0,
          sx: (theme) => ({
            mt: 1,
            p: 0,
            width: 'auto',
            borderRadius: 3,
            backgroundColor: "transparent",
            backdropFilter: "blur(12px)",
            border: `1px solid ${
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.08)"
                : "rgba(0,0,0,0.08)"
            }`,
            boxShadow: theme.palette.mode === "dark"
              ? "0 2px 8px rgba(0,0,0,0.1)"
              : "0 2px 8px rgba(0,0,0,0.05)",
          }),
        }}
      >
        <MenuItem
          onClick={() => { onClose(); navigate('/create-story'); setActive('create-story'); }}
          sx={(theme) => ({
            px: 3,
            py: 1.25,
            backgroundColor: 'transparent',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            transition: 'all 0.2s ease',
            borderRadius: 1,
            mx: 1,
            my: 0.5,
            position: 'relative',
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.05)'
                : 'rgba(0,0,0,0.04)',
              '& .menu-text-wrapper::after': {
                transform: 'scaleX(1)',
              },
            },
            '& .menu-text-wrapper': {
              position: 'relative',
              display: 'inline-block',
              '&::after': {
                content: '""',
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: -2,
                height: '2px',
                background: theme.palette.mode === 'dark'
                  ? 'linear-gradient(90deg, #ffffff 0%, #a5b4fc 100%)'
                  : 'linear-gradient(90deg, #1e293b 0%, #3b82f6 100%)',
                borderRadius: '1px',
                transform: 'scaleX(0)',
                transformOrigin: 'left center',
                transition: 'transform 0.2s ease',
              },
            },
          })}
        >
          <IconGradient gradientVariant="default">
            <EditNoteOutlinedIcon fontSize="small" />
          </IconGradient>
          <Box className="menu-text-wrapper">
            <SmallTextGradient variant="body2" gradientVariant="default" sx={{ fontWeight: 600 }}>
              New Story
            </SmallTextGradient>
          </Box>
        </MenuItem>
        {userId && (
          <>
          <MenuItem
            onClick={() => { onClose(); navigate('/my-stories'); setActive('mystories'); }}
            sx={(theme) => ({
              px: 3,
              py: 1.25,
              backgroundColor: 'transparent',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              transition: 'all 0.2s ease',
              borderRadius: 1,
              mx: 1,
              my: 0.5,
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.05)'
                  : 'rgba(0,0,0,0.04)',
                transform: 'translateX(4px)',
                '& .MuiBox-root': {
                  transform: 'scale(1.1)',
                },
              },
            })}
          >
            <IconGradient gradientVariant="default">
              <LibraryBooksOutlinedIcon fontSize="small" />
            </IconGradient>
            <Box className="menu-text-wrapper">
              <SmallTextGradient variant="body2" gradientVariant="default" sx={{ fontWeight: 600 }}>
                Go to My Stories
              </SmallTextGradient>
            </Box>
          </MenuItem>
            <MenuItem
              onClick={() => { onClose(); navigate('/my-bookmarks'); setActive('mybookmarks'); }}
              sx={(theme) => ({
                px: 3,
                py: 1.25,
                backgroundColor: 'transparent',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                transition: 'all 0.2s ease',
                borderRadius: 1,
                mx: 1,
                my: 0.5,
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.05)'
                    : 'rgba(0,0,0,0.04)',
                  transform: 'translateX(4px)',
                  '& .MuiBox-root': {
                    transform: 'scale(1.1)',
                  },
                },
              })}
            >
              <IconGradient gradientVariant="default">
                <BookmarkIcon fontSize="small" />
              </IconGradient>
              <Box>
                <SmallTextGradient variant="body2" gradientVariant="default" sx={{ fontWeight: 600 }}>
                  My Bookmarks
                </SmallTextGradient>
              </Box>
            </MenuItem>
          </>
        )}
        <MenuItem
          onClick={() => { onClose(); navigate('/explore'); setActive('explore'); }}
          sx={(theme) => ({
            px: 3,
            py: 1.25,
            backgroundColor: 'transparent',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            transition: 'all 0.2s ease',
            borderRadius: 1,
            mx: 1,
            my: 0.5,
            position: 'relative',
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.05)'
                : 'rgba(0,0,0,0.04)',
              '& .menu-text-wrapper::after': {
                transform: 'scaleX(1)',
              },
            },
            '& .menu-text-wrapper': {
              position: 'relative',
              display: 'inline-block',
              '&::after': {
                content: '""',
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: -2,
                height: '2px',
                background: theme.palette.mode === 'dark'
                  ? 'linear-gradient(90deg, #ffffff 0%, #a5b4fc 100%)'
                  : 'linear-gradient(90deg, #1e293b 0%, #3b82f6 100%)',
                borderRadius: '1px',
                transform: 'scaleX(0)',
                transformOrigin: 'left center',
                transition: 'transform 0.2s ease',
              },
            },
          })}
        >
          <IconGradient gradientVariant="default">
            <SearchIcon fontSize="small" />
          </IconGradient>
          <Box className="menu-text-wrapper">
            <SmallTextGradient variant="body2" gradientVariant="default" sx={{ fontWeight: 600 }}>
              Discover Stories
            </SmallTextGradient>
          </Box>
        </MenuItem>
        {userId && (
          <MenuItem
            onClick={() => { onClose(); navigate('/dashboard'); setActive('dashboard'); }}
            sx={(theme) => ({
              px: 3,
              py: 1.25,
              backgroundColor: 'transparent',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              transition: 'all 0.2s ease',
              borderRadius: 1,
              mx: 1,
              my: 0.5,
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.05)'
                  : 'rgba(0,0,0,0.04)',
                transform: 'translateX(4px)',
                '& .MuiBox-root': {
                  transform: 'scale(1.1)',
                },
              },
            })}
          >
            <IconGradient gradientVariant="default">
              <DashboardOutlinedIcon fontSize="small" />
            </IconGradient>
            <Box className="menu-text-wrapper">
              <SmallTextGradient variant="body2" gradientVariant="default" sx={{ fontWeight: 600 }}>
                Open Dashboard
              </SmallTextGradient>
            </Box>
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default CommandPalette;


